/**
 * Periodic sweep that evicts unit entities whose last position update is
 * older than {@link FADE_END_MS}. Opacity during the fade window is
 * applied at render time via {@link computeOpacity}; this hook only
 * performs the "age out and remove from store" side of the policy.
 *
 * Scope: units only. Markers (user-placed annotations) live in a separate
 * slice and are intentionally untouched.
 */

import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../mapSlice";
import { mapSlice } from "../mapSlice";
import { RootState } from "../../../store";
import { selectStaleIds, FADE_END_MS } from "../unitStalenessPolicy";

/** How often to re-evaluate unit age. 15s gives smooth fade without waste. */
export const STALE_TICK_INTERVAL_MS = 15_000;

export function useStaleUnitTicker(intervalMs: number = STALE_TICK_INTERVAL_MS): void {
    const dispatch = useAppDispatch();
    const units = useAppSelector((state: RootState) => state.map.value);

    useEffect(() => {
        const sweep = () => {
            const now = Date.now();
            const staleIds = selectStaleIds(
                units.map((u) => ({ id: u.id, lastUpdateMs: u.lastUpdateMs })),
                now,
                FADE_END_MS,
            );
            for (const id of staleIds) {
                dispatch(mapSlice.actions.removeEntity(id));
            }
        };

        // Sweep once on mount so entities that were already stale when the
        // map loaded are evicted without waiting a full interval.
        sweep();
        const handle = setInterval(sweep, intervalMs);
        return () => clearInterval(handle);
    }, [dispatch, units, intervalMs]);
}
